/**
 * ReportsDashboard (formerly ReportWorkflowPage)
 *
 * Main dashboard for viewing and managing reports.
 * Shows reports filtered by status tabs with deadline indicators.
 * Routes to /reports/:reportId which uses smart routing based on status.
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRoleAssumption } from '../../context/RoleAssumptionContext';
import {
  getOrgReports,
  getAnalystDrafts,
  getReportsForClientReview,
  type ReportWorkflow,
  type ReportWorkflowStatus,
} from '../../services/firestore';
import StatusBadge from '../../components/reports/StatusBadge';
import DeadlineBadge from '../../components/reports/DeadlineBadge';
import { useRatedInstitutions } from '../../hooks/useInstitutions';
import './ReportWorkflowPage.css';

type TabFilter = 'all' | ReportWorkflowStatus | 'overdue';

export default function ReportsDashboard() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const {
    effectiveOrganization,
    effectiveRole,
    effectiveIsPlatformAdmin,
    effectiveIsCRAAdmin,
    effectiveIsCRAAnalyst,
    effectiveIsRatingCommittee,
    effectiveIsBankAdmin,
  } = useRoleAssumption();
  const { t } = useTranslation();

  const [reports, setReports] = useState<ReportWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabFilter>('all');

  // Fetch institution data from BigQuery API
  const { institutions } = useRatedInstitutions();

  // Create a lookup map for institution names
  const institutionNameMap = useMemo(() => {
    const map = new Map<string, string>();
    institutions.forEach((inst) => map.set(inst.institution_id, inst.institution_name));
    return map;
  }, [institutions]);

  // Determine what reports to show based on role
  const isAdmin = effectiveIsPlatformAdmin || effectiveIsCRAAdmin;
  const isAnalyst = effectiveIsCRAAnalyst;
  const isCommittee = effectiveIsRatingCommittee;
  const isBankAdmin = effectiveIsBankAdmin;

  useEffect(() => {
    const loadReports = async () => {
      if (!userProfile?.uid) return;

      try {
        setLoading(true);
        setError(null);

        let fetchedReports: ReportWorkflow[] = [];

        if (isAdmin || isCommittee) {
          // Admins and committee see all org reports
          if (effectiveOrganization?.id) {
            fetchedReports = await getOrgReports(effectiveOrganization.id);
          }
        } else if (isAnalyst) {
          // Analysts see only their drafts
          fetchedReports = await getAnalystDrafts(userProfile.uid);
        } else if (isBankAdmin) {
          // Bank admins see reports for their institutions in client_review
          const institutionIds = effectiveOrganization?.institutions_access || [];
          fetchedReports = await getReportsForClientReview(institutionIds);
        }

        setReports(fetchedReports);
      } catch (err) {
        console.error('Error loading reports:', err);
        setError(t('reports.failedToLoadReports'));
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [userProfile?.uid, effectiveOrganization?.id, isAdmin, isAnalyst, isCommittee, isBankAdmin]);

  // Filter reports based on active tab
  const filteredReports = reports.filter((report) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'overdue') {
      const now = new Date();
      const dueDate = report.deadline.final_due_date.toDate();
      return dueDate < now && !['approved', 'rejected'].includes(report.status);
    }
    return report.status === activeTab;
  });

  // Count reports by status
  const statusCounts = reports.reduce(
    (acc, report) => {
      acc[report.status] = (acc[report.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  // Count overdue reports
  const now = new Date();
  const overdueCount = reports.filter((r) => {
    const dueDate = r.deadline.final_due_date.toDate();
    return dueDate < now && !['approved', 'rejected'].includes(r.status);
  }).length;

  const getInstitutionName = (institutionId: string): string => {
    return institutionNameMap.get(institutionId) || institutionId;
  };

  const formatPeriod = (period: string): string => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const formatUpdatedAt = (report: ReportWorkflow): string => {
    const updated = report.updated_at.toDate();
    const diffMs = Date.now() - updated.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Tabs to show based on role
  const getTabs = (): { key: TabFilter; label: string; count?: number }[] => {
    const tabs: { key: TabFilter; label: string; count?: number }[] = [];

    if (isAnalyst) {
      tabs.push({ key: 'draft', label: t('analyst.myDrafts'), count: statusCounts['draft'] || 0 });
      tabs.push({ key: 'pending_admin', label: t('status.submitted'), count: statusCounts['pending_admin'] || 0 });
      tabs.push({ key: 'client_review', label: t('status.clientReview'), count: statusCounts['client_review'] || 0 });
      tabs.push({ key: 'pending_committee', label: t('status.committee'), count: statusCounts['pending_committee'] || 0 });
      tabs.push({ key: 'approved', label: t('status.approved'), count: statusCounts['approved'] || 0 });
      tabs.push({ key: 'rejected', label: t('status.rejected'), count: statusCounts['rejected'] || 0 });
    } else if (isAdmin) {
      tabs.push({ key: 'all', label: t('reports.allReports'), count: reports.length });
      tabs.push({ key: 'draft', label: t('status.drafts'), count: statusCounts['draft'] || 0 });
      tabs.push({ key: 'pending_admin', label: t('reports.pendingAdmin'), count: statusCounts['pending_admin'] || 0 });
      tabs.push({ key: 'client_review', label: t('status.clientReview'), count: statusCounts['client_review'] || 0 });
      tabs.push({ key: 'pending_committee', label: t('status.pendingCommittee'), count: statusCounts['pending_committee'] || 0 });
      tabs.push({ key: 'approved', label: t('status.approved'), count: statusCounts['approved'] || 0 });
      tabs.push({ key: 'rejected', label: t('status.rejected'), count: statusCounts['rejected'] || 0 });
      if (overdueCount > 0) {
        tabs.push({ key: 'overdue', label: t('reports.overdue'), count: overdueCount });
      }
    } else if (isCommittee) {
      tabs.push({ key: 'pending_committee', label: t('reports.pendingApproval'), count: statusCounts['pending_committee'] || 0 });
      tabs.push({ key: 'approved', label: t('status.approved'), count: statusCounts['approved'] || 0 });
      tabs.push({ key: 'rejected', label: t('status.rejected'), count: statusCounts['rejected'] || 0 });
    } else if (isBankAdmin) {
      tabs.push({ key: 'client_review', label: t('status.pendingReview'), count: statusCounts['client_review'] || 0 });
    }

    return tabs;
  };

  const tabs = getTabs();

  // Set default tab based on role
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find((t) => t.key === activeTab)) {
      setActiveTab(tabs[0].key);
    }
  }, [tabs, activeTab]);

  if (loading) {
    return (
      <div className="workflow-page">
        <div className="loading">{t('reports.loadingReports')}</div>
      </div>
    );
  }

  return (
    <div className="workflow-page">
      <div className="workflow-header">
        <div className="workflow-header-content">
          <h1>{t('reports.title')}</h1>
          <p className="workflow-subtitle">
            {isAnalyst && t('reports.analystSubtitle')}
            {isAdmin && t('reports.adminSubtitle')}
            {isCommittee && t('reports.committeeSubtitle')}
            {isBankAdmin && t('reports.bankAdminSubtitle')}
          </p>
        </div>
        {/* New drafts are created from the Analyst Bench */}
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Status Tabs */}
      <div className="workflow-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`workflow-tab ${activeTab === tab.key ? 'active' : ''} ${tab.key === 'overdue' ? 'overdue-tab' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="tab-count">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Reports Table */}
      <div className="workflow-table-container">
        {filteredReports.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📄</span>
            <h3>{t('reports.noReports')}</h3>
            <p>
              {activeTab === 'all'
                ? t('reports.noReportsFound')
                : t('reports.noReportsWithStatus', { status: activeTab })}
            </p>
          </div>
        ) : (
          <table className="workflow-table">
            <thead>
              <tr>
                <th>{t('table.institution')}</th>
                <th>{t('table.period')}</th>
                <th>{t('table.status')}</th>
                <th>{t('table.dueDate')}</th>
                <th>{t('table.updated')}</th>
                <th>{t('table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.map((report) => (
                <tr key={report.id}>
                  <td className="institution-cell">
                    <span className="institution-name">
                      {getInstitutionName(report.institution_id)}
                    </span>
                  </td>
                  <td>{formatPeriod(report.period)}</td>
                  <td>
                    <StatusBadge status={report.status} size="small" />
                  </td>
                  <td>
                    <DeadlineBadge
                      dueDate={report.deadline.final_due_date}
                      isCompleted={report.status === 'approved'}
                      size="small"
                    />
                  </td>
                  <td className="updated-cell">{formatUpdatedAt(report)}</td>
                  <td>
                    <button
                      className="view-btn"
                      onClick={() => {
                        // Smart routing - ReportPage handles all status-based UI
                        navigate(`/reports/${report.id}`);
                      }}
                    >
                      {report.status === 'draft' && (isAnalyst || isAdmin)
                        ? t('common.edit')
                        : (report.status === 'pending_admin' && isAdmin) ||
                          (report.status === 'pending_committee' && (isCommittee || isAdmin))
                        ? t('common.review')
                        : report.status === 'client_review' && isBankAdmin
                        ? t('common.review')
                        : t('common.view')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
